import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { auth, db } from '../firebase';
import { doc, getDoc, updateDoc } from 'firebase/firestore';

const ChangePassword = () => {
  const navigate = useNavigate();

  const [currentPassword, setCurrentPassword] = useState('');
  const [newPassword, setNewPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');

  const [userUid, setUserUid] = useState('');
  const [loading, setLoading] = useState(false);

  // Custom message popup - no browser alert is used anywhere.
  const [popup, setPopup] = useState({
    show: false,
    type: '',
    title: '',
    message: ''
  });

  useEffect(() => {
    const uid =
      localStorage.getItem('nexora_user_uid') ||
      auth.currentUser?.uid;

    if (!uid) {
      navigate('/login');
      return;
    }

    setUserUid(uid);
  }, [navigate]);

  const showPopup = (type, title, message) => {
    setPopup({
      show: true,
      type,
      title,
      message
    });
  };

  const closePopup = () => {
    setPopup({
      show: false,
      type: '',
      title: '',
      message: ''
    });
  };

  const handleChangePassword = async (e) => {
    e.preventDefault();

    if (loading) return;

    if (!currentPassword || !newPassword || !confirmPassword) {
      showPopup(
        'error',
        'Incomplete',
        'Please fill in all three password fields.'
      );
      return;
    }

    if (newPassword.length < 6) {
      showPopup(
        'error',
        'Password Too Short',
        'New password must contain at least 6 characters.'
      );
      return;
    }

    if (newPassword !== confirmPassword) {
      showPopup(
        'error',
        'Passwords Do Not Match',
        'New password and confirm password must be the same.'
      );
      return;
    }

    if (currentPassword === newPassword) {
      showPopup(
        'error',
        'Choose a New Password',
        'Your new password must be different from the current password.'
      );
      return;
    }

    try {
      setLoading(true);

      const userRef = doc(db, 'users', userUid);
      const userSnapshot = await getDoc(userRef);

      if (!userSnapshot.exists()) {
        showPopup(
          'error',
          'Account Not Found',
          'Your account information could not be found.'
        );
        return;
      }

      const userData = userSnapshot.data();

      // Nexora currently keeps the account password in the users document.
      if (String(userData.password || '') !== String(currentPassword)) {
        showPopup(
          'error',
          'Incorrect Password',
          'The current password you entered is incorrect.'
        );
        return;
      }

      await updateDoc(userRef, {
        password: newPassword
      });

      setCurrentPassword('');
      setNewPassword('');
      setConfirmPassword('');

      showPopup(
        'success',
        'Successful',
        'Your password has been changed successfully.'
      );

    } catch (error) {
      console.error('Change password failed:', error);

      showPopup(
        'error',
        'Something Went Wrong',
        'Unable to change your password right now. Please try again.'
      );
    } finally {
      setLoading(false);
    }
  };

  return (
    <div style={styles.page}>
      <style>{`
        * {
          box-sizing: border-box;
          font-family: -apple-system, BlinkMacSystemFont,
            "Segoe UI", Roboto, sans-serif;
        }

        body {
          margin: 0;
          background: #fafafa;
        }

        .password-input {
          width: 100%;
          height: 54px;
          border: 1px solid #e2e8f0;
          border-radius: 16px;
          background: #f8fafc;
          padding: 0 16px;
          font-size: 15px;
          color: #0f172a;
          outline: none;
          transition: .2s;
        }

        .password-input:focus {
          border-color: #94a3b8;
          background: #ffffff;
          box-shadow: 0 0 0 3px rgba(148, 163, 184, .12);
        }

        .password-input::placeholder {
          color: #94a3b8;
        }

        .submit-btn {
          width: 100%;
          height: 54px;
          border: none;
          border-radius: 16px;
          background: #228b02;
          color: white;
          font-size: 15px;
          font-weight: 800;
          cursor: pointer;
          transition: .2s;
          margin-top: 8px;
        }

        .submit-btn:active {
          transform: scale(.98);
        }

        .submit-btn:disabled {
          opacity: .65;
          cursor: not-allowed;
        }

        .popup-overlay {
          position: fixed;
          inset: 0;
          background: rgba(15, 23, 42, .38);
          backdrop-filter: blur(5px);
          -webkit-backdrop-filter: blur(5px);
          display: flex;
          align-items: center;
          justify-content: center;
          padding: 22px;
          z-index: 9999;
        }

        .popup-card {
          width: 100%;
          max-width: 360px;
          background: #ffffff;
          border-radius: 26px;
          padding: 28px 22px 22px;
          text-align: center;
          box-shadow: 0 24px 70px rgba(15, 23, 42, .22);
          animation: popupIn .22s ease-out;
        }

        @keyframes popupIn {
          from {
            opacity: 0;
            transform: translateY(12px) scale(.96);
          }
          to {
            opacity: 1;
            transform: translateY(0) scale(1);
          }
        }

        .popup-icon {
          width: 64px;
          height: 64px;
          border-radius: 50%;
          margin: 0 auto 16px;
          display: flex;
          align-items: center;
          justify-content: center;
          font-size: 30px;
          font-weight: 800;
        }

        .popup-icon.success {
          background: #dcfce7;
          color: #16a34a;
        }

        .popup-icon.error {
          background: #fee2e2;
          color: #dc2626;
        }

        .popup-title {
          margin: 0;
          color: #0f172a;
          font-size: 20px;
          font-weight: 800;
        }

        .popup-message {
          margin: 8px 0 22px;
          color: #64748b;
          font-size: 14px;
          line-height: 1.5;
        }

        .popup-btn {
          width: 100%;
          height: 48px;
          border: none;
          border-radius: 14px;
          background: #1e293b;
          color: white;
          font-size: 14px;
          font-weight: 800;
          cursor: pointer;
        }
      `}</style>

      <div style={styles.card}>
        <div style={styles.header}>
          <button
            type="button"
            style={styles.backBtn}
            onClick={() => navigate('/settings')}
          >
            ←
          </button>

          <h2 style={styles.headerTitle}>
            Change Password
          </h2>

          <div style={{ width: 38 }} />
        </div>

        <div style={styles.content}>
          <div style={styles.topIcon}>
            🔐
          </div>

          <h1 style={styles.title}>
            Change Password
          </h1>

          <p style={styles.description}>
            Update your Nexora account password securely.
          </p>

          <form onSubmit={handleChangePassword}>
            <div style={styles.fieldGroup}>
              <label style={styles.label}>
                Current password
              </label>

              <input
                className="password-input"
                type="password"
                value={currentPassword}
                onChange={(e) => setCurrentPassword(e.target.value)}
                placeholder="Enter current password"
                autoComplete="current-password"
              />
            </div>

            <div style={styles.fieldGroup}>
              <label style={styles.label}>
                New password
              </label>

              <input
                className="password-input"
                type="password"
                value={newPassword}
                onChange={(e) => setNewPassword(e.target.value)}
                placeholder="Enter new password"
                autoComplete="new-password"
              />
            </div>

            <div style={styles.fieldGroup}>
              <label style={styles.label}>
                Confirm new password
              </label>

              <input
                className="password-input"
                type="password"
                value={confirmPassword}
                onChange={(e) => setConfirmPassword(e.target.value)}
                placeholder="Confirm new password"
                autoComplete="new-password"
              />
            </div>

            <button
              type="submit"
              className="submit-btn"
              disabled={loading}
            >
              {loading ? 'Changing Password...' : 'Change Password'}
            </button>
          </form>
        </div>
      </div>

      {popup.show && (
        <div
          className="popup-overlay"
          onClick={closePopup}
        >
          <div
            className="popup-card"
            onClick={(e) => e.stopPropagation()}
          >
            <div
              className={`popup-icon ${popup.type}`}
            >
              {popup.type === 'success' ? '✓' : '!'}
            </div>

            <h3 className="popup-title">
              {popup.title}
            </h3>

            <p className="popup-message">
              {popup.message}
            </p>

            <button
              type="button"
              className="popup-btn"
              onClick={closePopup}
            >
              OK
            </button>
          </div>
        </div>
      )}
    </div>
  );
};

const styles = {
  page: {
    minHeight: '100vh',
    background: '#fafafa',
    display: 'flex',
    justifyContent: 'center'
  },

  card: {
    width: '100%',
    maxWidth: '480px',
    minHeight: '100vh',
    background: '#f8fafc'
  },

  header: {
    height: '60px',
    background: '#ffffff',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between',
    padding: '0 16px',
    borderBottom: '1px solid #eeeeee'
  },

  backBtn: {
    width: '38px',
    height: '38px',
    background: 'none',
    border: 'none',
    fontSize: '27px',
    color: '#0f172a',
    cursor: 'pointer',
    padding: 0
  },

  headerTitle: {
    fontSize: '18px',
    fontWeight: '800',
    margin: 0,
    color: '#0f172a'
  },

  content: {
    padding: '34px 20px 30px'
  },

  topIcon: {
    width: '72px',
    height: '72px',
    borderRadius: '22px',
    background: '#e2e8f0',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    fontSize: '32px',
    margin: '0 auto 18px'
  },

  title: {
    textAlign: 'center',
    margin: '0',
    fontSize: '24px',
    fontWeight: '800',
    color: '#0f172a'
  },

  description: {
    textAlign: 'center',
    color: '#64748b',
    fontSize: '13px',
    lineHeight: '1.5',
    margin: '8px auto 28px',
    maxWidth: '310px'
  },

  fieldGroup: {
    marginBottom: '16px'
  },

  label: {
    display: 'block',
    color: '#334155',
    fontSize: '13px',
    fontWeight: '700',
    marginBottom: '8px',
    paddingLeft: '3px'
  }
};

export default ChangePassword;
